import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useFilterApplications,
  useSearchApplications
} from '@/lib/api/hooks/application';
import type { Application } from '@/lib/api/types';
import { ActionDropdown, TanStackTable, Loader, type ColumnDef, type DropdownAction,  Pagination, Button } from '@/components/ui';
import { MapPin,} from 'lucide-react';
import ApplicationsStats from './ApplicationsStats';
import { initialsCase, titleCase } from '@/utils/helpers/textTransform';
import { DocumentIcon } from '@/assets/icons';
import UpdateApplicationStatusModal from '@/pages/opportunities/applications/modals/UpdateApplicationStatusModal';
import Search from '@/components/ui/Search';
import FilterModule from '@/components/ui/FilterModule';
import FilterParamsDisplay from '@/components/ui/FilterParamsDisplay';
import { useApplicationsFilterSections } from '@/pages/opportunities/applications/FilterModule/FILTER_SECTIONS';
import { useSearchFilterParams } from '@/hooks/useSearchFilterParams';
import { Paragraph } from '@/components/ui/Typography';
import formatDate from '@/utils/helpers/formatDate';

type TabType = 'PUBLISH' | 'DRAFT' | 'ENDING_SOON';



const Applications: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('PUBLISH');
  const [updateStatusModalOpen, setUpdateStatusModalOpen] = useState(false);
  const [selectedApplicationForUpdateStatus, setSelectedApplicationForUpdateStatus] = useState<Application | null>(null);


  const FILTER_SECTIONS = useApplicationsFilterSections();
    // ── URL-driven search / filter state ──────────────────────────────────────
    const {
      mode,
      searchTerm,
      filterState,
      page,
      size,
      setSearch,
      setFilters,
      removeFilter,
      clearFilters,
      setPage,
      apiFilters,
      getActiveFilterCount
    } = useSearchFilterParams(FILTER_SECTIONS)
  


  // Search API (when searching)
  const { data: searchData, isLoading: isSearchLoading, isError: isSearchError, refetch: refetchSearch } = useSearchApplications(
    searchTerm,
    mode === 'search' 
  )

  // Filter API (when filtering or default)
  const { data: filterData, isLoading: isFilterLoading, isError: isFilterError, refetch: refetchFilter } = useFilterApplications(
    { page, size },
    apiFilters,
              mode === 'filter' || mode === 'default'
  )

  // Select appropriate data source: search takes priority
  const aggregateData = mode === 'search' ? searchData : filterData
  const isLoading = mode === 'search' ? isSearchLoading : isFilterLoading
  const isError = mode === 'search' ? isSearchError : isFilterError

  const totalPages = aggregateData?.data?.totalPage ?? 1
  const activeFilterCount = getActiveFilterCount()
  const refetchData = mode === 'search' ? refetchSearch : refetchFilter
  const emptyMessage = mode === 'search'
    ? 'No applications found matching your search criteria.'
    : mode === "filter" ?  'No applications found matching your filter criteria.'
    : 'No applications found.'


  const handleViewDetails = (application: Application) => {
    navigate(`/admin/applications/${application.applicationId}`);
  };

  const handleUpdateApplicationStatus = (application: Application) => {
    setSelectedApplicationForUpdateStatus(application);
    setUpdateStatusModalOpen(true);
  };

  const confirmUpdateStatus = () => {
      setUpdateStatusModalOpen(false);
          setSelectedApplicationForUpdateStatus(null);
          refetchData();
  };

  const getDropdownActions = (application: Application): DropdownAction[] => {
    const actions: DropdownAction[] = [
      {
        label: 'View Details',
        onClick: (e) => {
          e.stopPropagation();
          handleViewDetails(application);
        }
      },
      {
        label: 'Update Status',
        onClick: (e) => {
          e.stopPropagation();
          handleUpdateApplicationStatus(application);
        }
      }
    ];

    return actions;
  };

  // ── Columns ──
  const columns: ColumnDef<Application, unknown>[] = [
    {
      id: 'name',
      header: 'Candidate',
      cell: ({ row }) => {
        const application = row.original;
        const name = `${application.firstName ?? ''} ${application.lastName ?? ''}`;
        return (
          <div className="flex items-center gap-3 min-w-[220px]">
            <div className="flex h-10 w-10 shrink-0 font-medium items-center justify-center rounded-full bg-emerald-50">
              {initialsCase(name)}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 leading-tight line-clamp-2">{titleCase(name)}</p>
              <p className="text-sm text-gray-400 mt-0.5">{application.email}</p>
            </div>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4', cellClassName: 'px-4 py-4' },
    },
    {
      id: 'role',
      header: 'Applied Role',
      cell: ({ row }) => {
        const application = row.original;
        const jobTitle = application.jobTitle ?? '-';
        return (
          <div className="flex flex-col gap-2 min-w-[140px]">
            
            <span className="text-sm text-gray-900 whitespace-nowrap">{jobTitle}</span>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4', cellClassName: 'px-4 py-4 whitespace-nowrap' },
    },
        {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const application = row.original;
        const status = application.status;
        let label: string;
        let className: string;

        switch (status) {
          case 'NEW':
            label = 'New'; className = 'bg-blue-50 text-blue-700 border-blue-200';
            break;
          case 'REVIEW':
            label = 'In Review'; className = 'bg-indigo-50 text-indigo-700 border-indigo-200';
            break;
          case 'SHORTLISTED':
            label = 'Shortlisted'; className = 'bg-emerald-50 text-emerald-700 border-emerald-200';
            break;
          case 'INTERVIEW':
            label = 'Interview'; className = 'bg-purple-50 text-purple-700 border-purple-200';
            break;
          case 'OFFERED':
            label = 'Offered'; className = 'bg-amber-50 text-amber-700 border-amber-200';
            break;
          case 'HIRED':
            label = 'Hired'; className = 'bg-emerald-50 text-emerald-700 border-emerald-200';
            break;
          case 'REJECTED':
            label = 'Rejected'; className = 'bg-red-50 text-red-700 border-red-200';
            break;
          case 'WITHDRAWN':
            label = 'Withdrawn'; className = 'bg-gray-50 text-gray-700 border-gray-200';
            break;
          case 'ON_HOLD':
            label = 'On Hold'; className = 'bg-yellow-50 text-yellow-700 border-yellow-200';
            break;
          default:
            label = status; className = 'bg-gray-50 text-gray-700 border-gray-200';
        }
        return (
          <span className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium ${className}`}>
            {label}
          </span>
        );
      },
      meta: { headerClassName: 'px-4 py-4', cellClassName: 'px-4 py-4 whitespace-nowrap' },
    },
    {
      id: 'location',
      header: 'Location',
      cell: ({ row }) => (
        <span className="text-sm text-gray-700 whitespace-nowrap flex items-center gap-2"> <MapPin className="h-4 w-4 shrink-0 text-gray-400" /> {row.original.country}</span>
      ),
      meta: { headerClassName: 'px-4 py-4', cellClassName: 'px-4 py-4 whitespace-nowrap' },
    },
    {
      id: 'dateApplied',
      header: 'Date Applied',
      cell: ({ row }) => (
        <span className="text-sm text-gray-700 whitespace-nowrap">{formatDate(row.original.createdAt, "DD MMM YYYY")}</span>
      ),
      meta: { headerClassName: 'px-4 py-4', cellClassName: 'px-4 py-4 whitespace-nowrap' },
    },
    {
      id: 'resume',
      header: 'Resume',
      cell: ({ row }) => {
        const resumeUrl = row.original.cvUrl;
        return resumeUrl ? (
         
          <a
            href={resumeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:underline whitespace-nowrap flex items-center gap-1"
          >
            <DocumentIcon className="h-8 w-8  inline-block" />
            View Resume
          </a>
        ) : (
          <span className="text-sm text-gray-400 whitespace-nowrap">N/A</span>
        );
      },
      meta: { headerClassName: 'px-4 py-4', cellClassName: 'px-4 py-4 whitespace-nowrap' },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const application = row.original;
        return (
          <div onClick={(e) => e.stopPropagation()} className="flex justify-center">
            <ActionDropdown id={application.applicationId} actions={getDropdownActions(application)} />
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4 text-center', cellClassName: 'px-4 py-4 text-center whitespace-nowrap' },
    },
  ];

  return (
    <div className="space-y-4 flex flex-col gap-4">
      {/* Applications Stats */}
      <ApplicationsStats />
      {/* Tabs */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-0">
          <button
            onClick={() => {
              setActiveTab('PUBLISH');
              setPage(1);
            }}
            className={`px-6 py-2.5 text-sm font-semibold transition-colors rounded-md ${activeTab === 'PUBLISH'
              ? 'bg-wine-100 text-white'
              : 'text-gray-700 hover:bg-gray-100'
              }`}
          >
            All
          </button>
       
        </div>
      </div>

                   {/* Search + Filter Controls */}
          <div className="flex flex-col gap-4 p-4 border border-gray-200 rounded-md bg-white shadow-sm">
            <div className="flex items-center relative gap-4 justify-between flex-wrap">
              <Search
                value={searchTerm}
                onSearch={setSearch}
                placeholder="Search applications..."
              />
               <FilterModule
                    sections={FILTER_SECTIONS}
                    activeFilters={filterState}
                    onApply={setFilters}
                    activeFilterCount={activeFilterCount}
                  />
            </div>

            {/* Active filter chips — only shown in filter mode */}
            <FilterParamsDisplay
              sections={FILTER_SECTIONS}
              filterState={filterState}
              onRemove={removeFilter}
              onClearAll={clearFilters}
            />
          </div>

      {/* Applications Table */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 flex items-center justify-center">
            <Loader size="md" variant="wine" message="Loading applications..." />
          </div>
        ) : isError ? (
          <div className="p-8 flex items-center justify-center  flex-col gap-4 text-center">
            <Paragraph className="">Error loading applications.</Paragraph>
            <Button
              onClick={() => {
                refetchData();
              }}
              className=""
            >
              Retry
            </Button>
          </div>
        ) : (
          <>
                <div className="overflow-x-auto">
            <TanStackTable<Application>
              data={aggregateData?.data?.data ?? []}
              columns={columns}
              getRowId={(row) => row.applicationId}
              onRowClick={(row) => handleViewDetails(row)}
              tableClassName="min-w-[1200px] w-full divide-y divide-gray-200"
              theadClassName="bg-[#FAFAFA]"
              tbodyClassName="bg-white divide-y divide-gray-200"
              rowClassName="hover:bg-gray-50 cursor-pointer transition-colors"
              emptyMessage={emptyMessage}
              emptyClassName="px-6 py-12 text-center text-sm text-gray-500"
            />
          </div>
                <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              className="p-4"
            />
          </>
    
        )}
      </div>
  
      {/* Update Application Status Modal */}
      <UpdateApplicationStatusModal
        isOpen={updateStatusModalOpen}
        onClose={() => setUpdateStatusModalOpen(false)}
        onSuccess={confirmUpdateStatus}
        selectedApplication={selectedApplicationForUpdateStatus?.applicationId ? selectedApplicationForUpdateStatus.applicationId : null}
      />

     
    </div>
  );
};

export default Applications;

